import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { BookOpen, Brain, Users, GraduationCap, Phone, CheckCircle2, Sparkles } from 'lucide-react';

const LandingPage = () => {
  const [whatsappNumber, setWhatsappNumber] = useState('5521970694305');

  const handleCTA = () => {
    const message = encodeURIComponent('Olá! Gostaria de saber mais sobre a Formação em Psicanálise de 12 meses.');
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  const tripePillars = [
    {
      icon: <BookOpen className="w-12 h-12" />,
      title: 'Estudos Teóricos Aplicados',
      description: 'Você vai dominar os pilares fundamentais da psicanálise, indo muito além de conceitos soltos. Aqui, tudo se conecta: Associação Livre, Primeira tópica (Inconsciente, Pré-consciente), Segunda tópica (Id, Ego e Superego)... e uma imersão completa nas estruturas que sustentam a prática clínica.'
    },
    {
      icon: <Brain className="w-12 h-12" />,
      title: 'Análise Pessoal – O encontro com você mesmo',
      description: 'Ao longo da formação, você vivencia 20 horas de análise pessoal, um processo profundo de autoconhecimento que expande a sua capacidade de escuta, aprofunda sua sensibilidade e acelera seu crescimento como futuro psicanalista.'
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Supervisão Clínica Gratuita',
      description: 'São 10 horas de supervisão em grupo, 100% gratuitas, onde você aprende com casos reais, tira dúvidas, compartilha experiências e fortalece sua confiança para atuar profissionalmente.'
    }
  ];

  const curriculum = [
    'História da Psicanálise',
    'Teorias Essenciais',
    'Aparelho Psíquico',
    'Libido, Pulsões e Sexualidade',
    'Sonhos, Simbolismos e Representações',
    'Complexo de Édipo',
    'Mecanismos de Defesa',
    'Mito de Narciso',
    'Estruturas Clínicas',
    'Psicanálise por Freud, Jung, Lacan',
    'Klein, Winnicott e Bion',
    'Mitologia e Psicanálise',
    'Filosofia e Psicanálise',
    '...e muito mais'
  ];

  const presencialBenefits = [
    { icon: <Sparkles />, text: 'Um ambiente seguro, inspirador e cheio de significado' },
    { icon: <Users />, text: 'Interações que ampliam sua visão e fortalecem sua prática' },
    { icon: <BookOpen />, text: 'Conteúdos aprofundados com acompanhamento direto do professor' },
    { icon: <Brain />, text: 'Relacionamentos que se tornam parte da sua jornada profissional' }
  ];

  const faqs = [
    {
      question: 'Quais os benefícios de estudar psicanálise?',
      answer: 'Estudar psicanálise amplia sua percepção do ser humano, desenvolve uma escuta madura, fortalece sua autoridade emocional e abre portas para atuar de forma profunda com pessoas reais. É uma formação que transforma a mente — e, por consequência, transforma a vida.'
    },
    {
      question: 'A profissão de Psicanalista é reconhecida?',
      answer: 'Sim. O psicanalista é um profissional de livre exercício, amparado pelos artigos 5º, XIII, e 170 da Constituição Federal, que garantem o direito à atividade profissional autônoma. A psicanálise possui regulamentação própria e é reconhecida como prática independente.'
    },
    {
      question: 'O certificado é reconhecido pelo MEC?',
      answer: 'O MEC não reconhece cursos de formação em Psicanálise porque, segundo a Portaria MEC nº 2.253/2001, cursos livres não precisam de autorização ou reconhecimento do Ministério da Educação. Isso quer dizer: a psicanálise é uma profissão independente, e seu exercício não depende do MEC, mas sim da qualidade da formação — exatamente o que você terá aqui: teoria, análise pessoal e supervisão clínica.'
    },
    {
      question: 'Qual a metodologia de ensino?',
      answer: 'Nossa formação segue o Tripé Psicanalítico, padrão internacional: Teoria Aplicada, Análise Pessoal – 20h, e Supervisão Clínica – 10h.'
    },
    {
      question: 'Preciso ser formado em Psicologia ou Medicina?',
      answer: 'Não. A psicanálise é aberta a todos que desejam compreender a mente humana ou atuar na clínica. Profissionais de diversas áreas também buscam essa formação para enriquecer sua prática.'
    },
    {
      question: 'Como é o mercado de trabalho do Psicanalista?',
      answer: 'O mercado é crescente e cheio de oportunidades. Hoje, a procura por atendimento emocional está maior do que nunca. Você poderá atuar: Em consultórios (presencial ou online), Em escolas, Em instituições e empresas, Em projetos sociais, De forma autônoma e independente. Com uma boa formação, há espaço real para construir carreira e renda.'
    },
    {
      question: 'Como funciona a afiliação dos alunos formados?',
      answer: 'A afiliação não é obrigatória, mas oferecemos filiação para os alunos que desejarem. Ela serve como suporte profissional, validação institucional e acesso a benefícios exclusivos — sempre como uma escolha, nunca uma exigência.'
    },
    {
      question: 'E se eu não quiser clinicar?',
      answer: 'Tudo bem. Muitos alunos estudam psicanálise para: Crescimento pessoal, Melhorar relações, Entender emoções, Aprofundar crenças e comportamentos, Qualificar sua área de atuação. Mesmo sem clínica, a formação já traz transformações profundas.'
    }
  ];

  return (
    <div className="landing-page">
      {/* Header Images */}
      <div className="header-images">
        <img 
          src="/images/header-wide.png" 
          alt="Header" 
          className="header-image header-wide"
        />
        <img 
          src="/images/header-mobile.png" 
          alt="Header" 
          className="header-image header-mobile"
        />
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Sua transformação começa na mente.
              <span className="highlight-text"> Agora é a sua vez de se tornar um Especialista em Psicanálise</span>
              <span className="duration-badge"> Em Apenas 12 Meses!</span>
            </h1>
            <p className="hero-subtitle">
              Você sente que nasceu para ajudar pessoas, mas ainda falta a base sólida, 
              a estrutura teórica e a vivência prática que só uma formação séria entrega?
            </p>
            <p className="hero-cta-text">
              Então respira fundo e vem comigo: esse é o passo que separa quem deseja… de quem realiza.
            </p>
            <Button onClick={handleCTA} className="cta-button cta-button-large">
              <Phone className="mr-2" /> Garanta Sua Vaga Agora
            </Button>
          </div>
          <div className="hero-image">
            <img 
              src="/images/estudantes-ultima-turma.png" 
              alt="Estudantes de Psicanálise na última turma" 
              className="rounded-image"
            />
          </div>
        </div>
      </section>

      {/* Tripé Freudiano Section */}
      <section className="tripe-section">
        <div className="section-container">
          <h2 className="section-title">O TRIPÉ QUE FORMA VERDADEIROS PROFISSIONAIS</h2>
          <p className="section-subtitle">
            A base metodológica que diferencia nossa formação
          </p>
          
          <div className="pillars-grid">
            {tripePillars.map((pillar, index) => (
              <Card key={index} className="pillar-card">
                <CardContent className="pillar-content">
                  <div className="pillar-icon">
                    {pillar.icon}
                  </div>
                  <h3 className="pillar-title">{pillar.title}</h3>
                  <p className="pillar-description">{pillar.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <p className="tripe-reinforcement">
            Essa tríade teórica–pessoal–clínica é a base que diferencia quem apenas estuda… 
            de quem realmente se torna psicanalista.
          </p>
        </div>
      </section>

      {/* Curriculum Section */}
      <section className="curriculum-section">
        <div className="section-container">
          <h2 className="section-title">FORMAÇÃO COMPLETA EM PSICANÁLISE CLÍNICA</h2>
          <p className="section-subtitle">
            Durante 12 meses, você mergulhará em conteúdos profundos, atuais e indispensáveis:
          </p>
          
          <div className="curriculum-grid">
            {curriculum.map((item, index) => (
              <div key={index} className="curriculum-item">
                <CheckCircle2 className="check-icon" />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <p className="curriculum-benefit">
            Uma formação completa, estruturada e transformadora — feita para quem quer 
            entrar na clínica com segurança, profundidade e excelência.
          </p>
          
          <Button onClick={handleCTA} className="cta-button cta-button-medium">
            <Phone className="mr-2" /> Inscreva-se Agora
          </Button>
        </div>
      </section>

      {/* Video Section */}
      <section className="video-section">
        <div className="section-container">
          <h2 className="section-title">VEJA COMO FOI NOSSA ÚLTIMA TURMA</h2>
          <p className="section-subtitle">
            Conheça mais sobre nossa metodologia e transformação na última turma
          </p>
          
          <div className="video-container">
            <video
              controls
              width="400"
              className="video-player"
              poster="/iamges/capa-video.png"
            >
              <source src="/videos/video-para-landingpage.mp4" type="video/mp4" />
              Seu navegador não suporta vídeos.
            </video>
          </div>
        </div>
      </section>

      {/* Presencial Section */}
      <section className="presencial-section">
        <div className="section-container">
          <div className="presencial-header">
            <GraduationCap className="presencial-icon" />
            <h2 className="section-title">Aulas 100% Presenciais — Uma Experiência Que Transforma de Verdade</h2>
          </div>
          
          <div className="presencial-content">
            <div className="presencial-text">
              <p className="presencial-intro">
                Nada substitui o olhar, a troca, a energia do presencial.
              </p>
              <p>
                E é por isso que nossa formação acontece totalmente ao vivo, <strong>uma vez por semana</strong>, 
                para que você viva a psicanálise na prática, do jeito que ela nasceu para ser aprendida.
              </p>
              <p>
                Aqui, cada encontro se torna um momento de aprofundamento, conexão e crescimento real. 
                Você participa, pergunta, debate, experimenta, sente — e isso acelera seu desenvolvimento 
                de um jeito que o online não consegue entregar.
              </p>
              
              <div className="benefits-list">
                {presencialBenefits.map((benefit, index) => (
                  <div key={index} className="benefit-item">
                    <span className="benefit-icon">{benefit.icon}</span>
                    <span>{benefit.text}</span>
                  </div>
                ))}
              </div>
              
              <p className="presencial-conclusion">
                Se você quer viver a psicanálise de forma intensa, humana e profunda… 
                o presencial é o seu lugar.
              </p>
            </div>
            
            <div className="presencial-image">
              <img 
                src="/images/sessao-psicanalise-clinica2.png" 
                alt="Ambiente de Análise" 
                className="rounded-image"
              />
            </div>
          </div>
          
          <p className="presencial-conclusion">
            Você quer estudar onde a teoria encontra a experiência?
          </p>

          <Button onClick={handleCTA} className="cta-button cta-button-medium">
            <Phone className="mr-2" />Sim, eu quero!
          </Button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-container">
          <h2 className="section-title">Perguntas Frequentes</h2>
          <p className="section-subtitle">
            Tire suas dúvidas sobre a formação
          </p>
          
          <Accordion type="single" collapsible className="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="faq-item">
                <AccordionTrigger className="faq-question">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="section-container">
          <h2 className="final-cta-title">
            Pronto para dar o próximo passo?
          </h2>
          <p className="final-cta-text">
            Sua jornada começa com uma decisão — e essa decisão é agora.
          </p>
          <Button onClick={handleCTA} className="cta-button cta-button-large">
            <Phone className="mr-2" /> Faça Parte da Próxima Turma
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p>© 2025 Formação em Psicanálise. Todos os direitos reservados.</p>
          <p className="footer-note">
            Sua transformação profissional começa aqui.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;